import { delay } from '@libs/utils/helpers/delay';
import { useState, useRef, useEffect, useCallback } from 'react';

interface UseCreditCarouselProps {
  childrenCount: number;
  loading?: boolean;
}

export const useCreditCarousel = ({ childrenCount, loading }: UseCreditCarouselProps) => {
  const [scrollSteps, setScrollSteps] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [groupIndices, setGroupIndices] = useState<number[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const carouselRef = useRef<HTMLDivElement>(null);

  const calculateGroups = useCallback(() => {
    if (!carouselRef.current) return;

    const visibleCount = Math.floor(carouselRef.current.clientWidth / (carouselRef.current.firstElementChild as HTMLDivElement).clientWidth);
    const maxVisible = visibleCount >= 4 ? 3 : visibleCount;
    if (visibleCount <= 0) return;

    const groups: number[] = [];
    for (let i = 0; i < childrenCount; i += maxVisible) {
      groups.push(i);
    }

    setGroupIndices(groups);
    setScrollSteps(groups.length);
  }, [childrenCount]);

  useEffect(() => {
    if (loading) return;

    calculateGroups();
    window.addEventListener('resize', calculateGroups);

    return () => {
      window.removeEventListener('resize', calculateGroups);
    };
  }, [loading, calculateGroups]);

  useEffect(() => {
    const handleScroll = () => {
      if (!carouselRef.current || groupIndices.length === 0) return;

      const carouselBounds = carouselRef.current.getBoundingClientRect();
      let closestPage = currentPage;

      groupIndices.forEach((index, page) => {
        const childElement = carouselRef.current!.children[index] as HTMLDivElement;
        const childBounds = childElement.getBoundingClientRect();

        if (childBounds.left >= carouselBounds.left && childBounds.right <= carouselBounds.right) {
          closestPage = page;
        }
      });

      if (closestPage !== currentPage) {
        setCurrentPage(closestPage);
      }
    };

    carouselRef.current?.addEventListener('scroll', handleScroll);
    return () => {
      carouselRef.current?.removeEventListener('scroll', handleScroll);
    };
  }, [groupIndices, currentPage]);

  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    const handleMouseDown = (e: MouseEvent) => {
      setIsDragging(true);
      setStartX(e.pageX - carousel.offsetLeft);
      setScrollLeft(carousel.scrollLeft);
      document.body.style.userSelect = 'none';
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;

      e.preventDefault();
      const x = e.pageX - carousel.offsetLeft;
      const walk = (x - startX) * 1.5;
      carousel.scrollLeft = scrollLeft - walk;
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.body.style.userSelect = '';
    };

    const handleMouseLeave = handleMouseUp;

    carousel.addEventListener('mousedown', handleMouseDown);
    carousel.addEventListener('mousemove', handleMouseMove);
    carousel.addEventListener('mouseup', handleMouseUp);
    carousel.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      carousel.removeEventListener('mousedown', handleMouseDown);
      carousel.removeEventListener('mousemove', handleMouseMove);
      carousel.removeEventListener('mouseup', handleMouseUp);
      carousel.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [isDragging, startX, scrollLeft]);

  const handleManualScroll = useCallback(
    async (direction: number) => {
      if (!carouselRef.current || groupIndices.length === 0) return;

      const newIndex = Math.min(Math.max(currentPage + direction, 0), groupIndices.length - 1);

      const targetIndex = groupIndices[newIndex];
      const childElement = carouselRef.current.children[targetIndex] as HTMLDivElement;

      carouselRef.current.scrollTo({
        left: childElement.offsetLeft,
        behavior: 'smooth',
      });

      await delay(500);
      setCurrentPage(newIndex);
    },
    [currentPage, groupIndices]
  );

  return {
    carouselRef,
    scrollSteps,
    currentPage,
    isDragging,
    handleManualScroll,
  };
};
